import { useEffect, useState } from 'react';
import { Search, UserPlus, UserRound, Gift, ArrowLeft, IdCard } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { useCustomerSearch, useRegisterCustomer } from '../useOutpatient';
import { Empty } from './ui';
import type { CounterCustomer } from '../types';

/**
 * Step 1 of the counter: who is this person?
 *
 * The important design point is that this searches the hospital's ONE patient
 * directory — by mobile, name or UHID — before it will offer to register
 * anybody. A very large share of pharmacy walk-ins are not new customers at
 * all: they consulted here once, were prescribed a lifelong medicine, and now
 * come back every month for a refill. Registering them again would split one
 * person into two records, strand their loyalty points on whichever half the
 * operator happened to pick, and hide the purchase from the Billing tab of the
 * record their doctor actually looks at.
 *
 * So a registered patient is found and reused; only someone genuinely new is
 * created, and even then as a real `patients` row with a real UHID, so the same
 * person arriving for a consultation next month is already registered.
 *
 * ── Why "Register" is deliberately hard to reach ──
 * It appears only AFTER a search has actually run and come back with nothing.
 * A register button sitting next to the search box from the start is the single
 * easiest way to end up with duplicate patient records: it is faster to type a
 * name than to wait for a lookup, so that is what a busy till does.
 */

/** One search hit. Its own component so the row's markup stays readable. */
function CustomerRow({
  customer, loyaltyEnabled, onSelect,
}: {
  customer: CounterCustomer;
  loyaltyEnabled: boolean;
  onSelect: () => void;
}) {
  // Listed, so the operator sees why, but not selectable: the server refuses
  // a new sale for a deceased patient anyway. Returns of earlier purchases are
  // on the Sales & Returns tab and do not go through this picker.
  const deceased = !!customer.deceased;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={deceased}
      title={deceased ? 'Recorded as deceased — a new sale cannot be made on this record' : undefined}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-left ' +
        'transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
        deceased ? 'cursor-not-allowed opacity-60' : 'hover:border-teal hover:bg-accent',
      )}
    >
      <span className="grid h-8 w-8 shrink-0 place-content-center rounded-full bg-muted text-ink-muted group-hover:bg-card">
        <UserRound size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold">{customer.name}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-ink-muted">
          <span className="font-mono">{customer.uhid}</span>
          <span>{customer.mobile}</span>
          {customer.age ? <span>{customer.age}y</span> : null}
        </span>
        {/* Says what to do instead, not just that the row is off-limits. */}
        {deceased && (
          <span className="mt-1 block text-[11px] text-ink-muted">
            Recorded as deceased — new sales are blocked. For a refund of an earlier purchase, use Sales &amp; Returns.
          </span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        {/* Says out loud which kind of record this is — picking the right one
            is the entire job of this step. */}
        {deceased && <Badge variant="neutral">Deceased</Badge>}
        {customer.registration_source === 'visit' && <Badge variant="success">Patient</Badge>}
        {loyaltyEnabled && customer.points_balance ? <Badge variant="warn">{customer.points_balance} pts</Badge> : null}
      </span>
    </button>
  );
}

export default function CustomerPanel({
  customer,
  onSelect,
  onClear,
  loyaltyEnabled,
  deceased = false,
}: {
  customer: CounterCustomer | null;
  onSelect: (c: CounterCustomer) => void;
  onClear: () => void;
  loyaltyEnabled: boolean;
  /** The chosen customer's CURRENT status — from the fresh detail fetch, so a
   * death recorded after they were picked still shows here. */
  deceased?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [registering, setRegistering] = useState(false);
  const [draft, setDraft] = useState({ name: '', mobile: '', gender: 'Female', age: '' });

  const searchQuery = useCustomerSearch(debounced);
  const register = useRegisterCustomer();

  // Same 400ms debounce every other live-search box in this app uses — a till
  // operator types a 10-digit mobile number fast, and one request per keystroke
  // is nine wasted round trips.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query), 400);
    return () => clearTimeout(handle);
  }, [query]);

  const results = searchQuery.data ?? [];
  const searched = debounced.trim().length >= 2;
  const settled = searched && !searchQuery.isLoading;
  const nothingFound = settled && results.length === 0;

  // A mobile number typed into the search box is almost certainly the one the
  // new customer should be registered with — pre-fill it rather than making the
  // operator type it twice.
  const startRegister = () => {
    const looksLikeMobile = /^\d{6,}$/.test(query.trim());
    setDraft({
      name: looksLikeMobile ? '' : query.trim(),
      mobile: looksLikeMobile ? query.trim() : '',
      gender: 'Female',
      age: '',
    });
    setRegistering(true);
  };

  const submitRegister = () => {
    register.mutate(
      {
        name: draft.name.trim(),
        mobile: draft.mobile.trim(),
        gender: draft.gender,
        ...(draft.age ? { age: parseInt(draft.age, 10) } : {}),
      },
      {
        onSuccess: (created) => {
          setRegistering(false);
          setQuery('');
          setDebounced('');
          onSelect(created);
        },
      }
    );
  };

  const registerError = register.error instanceof ApiError ? register.error.message : null;

  // ── Chosen ─────────────────────────────────────────────────────
  // Collapses to a compact identity strip so the rest of the sale gets the
  // vertical space. Everything needed to confirm "yes, this is the right
  // person" stays on screen; nothing else does.
  if (customer) {
    return (
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-content-center rounded-full bg-accent text-teal">
            <UserRound size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{customer.name}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-ink-muted">
              <span className="font-mono">{customer.uhid}</span>
              <span>·</span>
              <span>{customer.mobile}</span>
              {customer.age ? <><span>·</span><span>{customer.age}y</span></> : null}
              {customer.gender ? <><span>·</span><span>{customer.gender}</span></> : null}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {deceased && <Badge variant="danger">Deceased</Badge>}
              {customer.registration_source === 'pharmacy' ? (
                <Badge variant="info" title="First registered at the pharmacy counter">Pharmacy customer</Badge>
              ) : (
                <Badge variant="success" title="Registered through the Visit Workflow — has a clinical record here">
                  Registered patient
                </Badge>
              )}
              {loyaltyEnabled && customer.points_balance != null && (
                <Badge variant="warn" className="inline-flex items-center gap-1">
                  <Gift size={11} /> {customer.points_balance} points
                </Badge>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClear} className="shrink-0">
            <ArrowLeft size={13} /> Change
          </Button>
        </div>
      </Card>
    );
  }

  // ── Registering ────────────────────────────────────────────────
  if (registering) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><UserPlus size={15} /> New customer</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setRegistering(false)}>Back to search</Button>
        </CardHeader>

        <div className="mb-3.5 flex gap-2.5 rounded-lg bg-info-soft px-3 py-2.5 text-[11.5px] text-info-ink">
          <IdCard size={15} className="mt-px shrink-0" />
          <p className="leading-relaxed">
            This creates a full patient record with its own UHID — the same record their doctor would
            see if they consult here later, and the one their loyalty points belong to.
          </p>
        </div>

        {registerError && (
          <div className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{registerError}</div>
        )}

        <div className="space-y-3">
          <FormField label="Name" required>
            <Input autoFocus value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </FormField>
          <FormField label="Mobile" required>
            <Input inputMode="numeric" value={draft.mobile} onChange={(e) => setDraft((d) => ({ ...d, mobile: e.target.value }))} />
          </FormField>
          <div className="grid2">
            <FormField label="Gender">
              <NativeSelect value={draft.gender} onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}>
                {['Female', 'Male', 'Other'].map((g) => <option key={g}>{g}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Age">
              <Input type="number" min={0} value={draft.age} onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))} />
            </FormField>
          </div>
        </div>

        <Button
          full
          className="mt-4"
          disabled={!draft.name.trim() || !draft.mobile.trim() || register.isPending}
          loading={register.isPending}
          onClick={submitRegister}
        >
          Register &amp; start the sale
        </Button>
      </Card>
    );
  }

  // ── Searching ──────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader>
        <CardTitle><Search size={15} /> Find the customer</CardTitle>
      </CardHeader>

      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <Input
          autoFocus
          value={query}
          placeholder="Mobile, name or UHID"
          className="pl-9"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <p className="mt-1.5 text-[11.5px] text-ink-muted">
        Search first — most walk-ins are already registered here, and their purchase should attach to
        the record they already have.
      </p>

      <div className={cn('mt-3.5 space-y-1.5', !searched && 'hidden')}>
        {searchQuery.isLoading && (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
            Searching…
          </div>
        )}

        {results.map((c) => (
          <CustomerRow key={c.id} customer={c} loyaltyEnabled={loyaltyEnabled} onSelect={() => onSelect(c)} />
        ))}

        {nothingFound && (
          <Empty
            label={`Nobody matches “${debounced}”`}
            hint="Check the spelling or try their mobile number before registering someone new."
          />
        )}
      </div>

      {/* Only offered once a search has actually returned — see the header. */}
      {settled && (
        <Button variant={nothingFound ? 'primary' : 'ghost'} size="sm" className="mt-3" onClick={startRegister}>
          <UserPlus size={13} /> Register a new customer
        </Button>
      )}
    </Card>
  );
}
